// @seventide/types — documents module DTOs (FR-2.1–FR-2.5, §9.1 OCR, §9.3).
// Owned by the documents module agent (see docs/ARCHITECTURE.md §6).
import { z } from 'zod';
import { Id, IsoDate, IsoDateTime } from './common';
import {
  DocumentCategory,
  EntityType,
  Stage,
  type ModuleVisibility,
  type VisibilityModule,
} from './enums';

// ---------------------------------------------------------------- files (FR-2.1)

/** Largest file we store (FR-2.1). */
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
/** Largest client-generated rendition (thumbnail / HEIC preview). */
export const MAX_RENDITION_BYTES = 3 * 1024 * 1024;

/** File kinds we accept, detected from the file's first bytes (magic bytes), not only the extension. */
export const DOCUMENT_FILE_KINDS = ['pdf', 'jpeg', 'png', 'heic', 'docx'] as const;
export type DocumentFileKind = (typeof DOCUMENT_FILE_KINDS)[number];

export const DOCUMENT_MIME_TYPES: Record<DocumentFileKind, string> = {
  pdf: 'application/pdf',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

/** `accept` attribute for file inputs (extensions + MIME types). */
export const DOCUMENT_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,.heic,.heif,.docx,application/pdf,image/jpeg,image/png,image/heic,image/heif,' +
  DOCUMENT_MIME_TYPES.docx;

export const DOCUMENT_FILE_KIND_LABELS: Record<DocumentFileKind, string> = {
  pdf: 'PDF',
  jpeg: 'Photo (JPEG)',
  png: 'Image (PNG)',
  heic: 'iPhone photo (HEIC)',
  docx: 'Word document',
};

/** Map a stored MIME type back to its kind (unknown → null). */
export function documentKindFromMime(mime: string | null | undefined): DocumentFileKind | null {
  const m = (mime ?? '').toLowerCase();
  if (m === 'application/pdf') return 'pdf';
  if (m === 'image/jpeg' || m === 'image/jpg') return 'jpeg';
  if (m === 'image/png') return 'png';
  if (m === 'image/heic' || m === 'image/heif') return 'heic';
  if (m === DOCUMENT_MIME_TYPES.docx) return 'docx';
  return null;
}

// ---------------------------------------------------------------- OCR (§9.1)

/**
 * Text extraction status. `pending` → queued, `processing` → being read now, `done` → text stored (encrypted),
 * `failed` → gave up after retries, `not_applicable` → no text can be extracted (e.g. a photo when no OCR
 * service is configured), `none` → legacy rows created before the pipeline existed.
 */
export const OCR_STATUSES = ['none', 'pending', 'processing', 'done', 'failed', 'not_applicable'] as const;
export const OcrStatus = z.enum(OCR_STATUSES);
export type OcrStatus = z.infer<typeof OcrStatus>;

export const OCR_STATUS_LABELS: Record<OcrStatus, string> = {
  none: 'Not read yet',
  pending: 'Waiting to be read',
  processing: 'Reading the text',
  done: 'Text found',
  failed: "Couldn't read the text",
  not_applicable: 'No text to search',
};

// ---------------------------------------------------------------- visibility (R-3.3)

/**
 * Categories whose contents belong to another module. A member who can't see that module can't see documents
 * in the category (e.g. a family member without Gifts visibility never sees gift evidence). Mirrors the SQL
 * function `app_document_category_module()` in migration 0200.
 */
export const DOCUMENT_CATEGORY_MODULE: Record<DocumentCategory, VisibilityModule | null> = {
  lpa_deputyship: null,
  will: null,
  bank_statement: 'money',
  pension_statement: 'money',
  care_home_contract: 'care',
  care_invoice: 'care',
  council_letter: null,
  nhs_letter: 'health',
  chc_checklist_dst: 'health',
  assessment_report: 'health', // special-category assessment content (security review SR-04; migration 0201)
  benefit_letter: null,
  gift_evidence: 'gifts',
  property: null,
  insurance: null,
  funeral_plan: null,
  other: null,
};

/** Categories that hold health information → special category data (R-5.2). Mirrors the SQL trigger. */
export const SPECIAL_CATEGORY_DOCUMENT_CATEGORIES: readonly DocumentCategory[] = [
  'nhs_letter',
  'chc_checklist_dst',
  'assessment_report',
];

/**
 * Module of each linkable record type; `null` = workspace-level records everyone in the workspace can see.
 * Mirrors the SQL function `app_entity_module()` in migration 0200 (which uses 'workspace' for null).
 */
export const ENTITY_TYPE_MODULE: Record<EntityType, VisibilityModule | null> = {
  gift: 'gifts',
  recipient: 'gifts',
  tax_year_summary: 'gifts',
  transaction: 'money',
  account: 'money',
  care_event: 'care',
  care_invoice: 'care',
  care_contract: 'care',
  health_entry: 'health',
  chc_process: 'health',
  benefit_claim: 'health',
  decision: 'attorney',
  attorney_expense: 'attorney',
  document: null,
  principal: null,
  legal_instrument: null,
  consent_record: null,
};

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  gift: 'Gift',
  recipient: 'Gift recipient',
  tax_year_summary: 'Tax year summary',
  transaction: 'Transaction',
  account: 'Account',
  care_event: 'Care event',
  care_invoice: 'Care invoice',
  care_contract: 'Care contract',
  health_entry: 'Health diary entry',
  chc_process: 'CHC assessment',
  benefit_claim: 'Benefit claim',
  decision: 'Decision',
  attorney_expense: 'Attorney expense',
  document: 'Document',
  principal: 'Principal profile',
  legal_instrument: 'Power of attorney or deputyship',
  consent_record: 'Consent record',
};

/** Sensible category to suggest when uploading a document from a record (e.g. a receipt for a transaction). */
export const DEFAULT_CATEGORY_FOR_ENTITY: Partial<Record<EntityType, DocumentCategory>> = {
  gift: 'gift_evidence',
  recipient: 'gift_evidence',
  tax_year_summary: 'bank_statement',
  transaction: 'bank_statement',
  account: 'bank_statement',
  care_event: 'council_letter',
  care_invoice: 'care_invoice',
  care_contract: 'care_home_contract',
  health_entry: 'nhs_letter',
  chc_process: 'chc_checklist_dst',
  benefit_claim: 'benefit_letter',
  decision: 'other',
  attorney_expense: 'other',
  legal_instrument: 'lpa_deputyship',
  consent_record: 'other',
};

/** Module key stored in `documents.link_modules` for workspace-level records. */
export const WORKSPACE_LINK_MODULE = 'workspace';

/**
 * Pure mirror of the RLS rule (`app_document_visible`): a document is visible when the member can see
 * Documents, the category's module (if any), and — when the document is linked to records — at least one of
 * the linked records' modules.
 */
export function isDocumentVisible(
  visibility: ModuleVisibility,
  category: DocumentCategory,
  linkModules: readonly string[],
): boolean {
  if (!visibility.documents) return false;
  const catModule = DOCUMENT_CATEGORY_MODULE[category];
  if (catModule && !visibility[catModule]) return false;
  if (linkModules.length === 0) return true;
  return linkModules.some((m) => m === WORKSPACE_LINK_MODULE || visibility[m as VisibilityModule] === true);
}

/** Can this member see records of this type? */
export function isEntityTypeVisible(visibility: ModuleVisibility, entityType: EntityType): boolean {
  const m = ENTITY_TYPE_MODULE[entityType];
  return m === null || visibility[m] === true;
}

// ---------------------------------------------------------------- tags

export const MAX_DOCUMENT_TAGS = 20;
export const DocumentTag = z
  .string()
  .trim()
  .min(1, 'Tags cannot be empty')
  .max(40, 'Keep each tag to 40 characters or fewer')
  .regex(/^[^,<>]+$/, 'Tags cannot contain commas or angle brackets');

/** Trim, collapse spaces and drop case-insensitive duplicates, keeping the first spelling. */
export function normaliseTags(tags: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const t = raw.replace(/\s+/g, ' ').trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

export const DocumentTags = z
  .array(DocumentTag)
  .max(MAX_DOCUMENT_TAGS, `Use ${MAX_DOCUMENT_TAGS} tags or fewer`)
  .transform((t) => normaliseTags(t));

/** Parse tags sent as a JSON array or a comma-separated string (multipart form fields). */
export function parseTagsField(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v !== 'string' || v.trim() === '') return [];
  const s = v.trim();
  if (s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s) as unknown;
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      /* fall through to comma separated */
    }
  }
  return s.split(',');
}

// ---------------------------------------------------------------- DTOs

export const DocumentTitle = z.string().trim().min(1, 'Enter a title').max(200, 'Keep the title to 200 characters or fewer');
export const DocumentNotes = z.string().max(4000, 'Keep notes to 4,000 characters or fewer');

export const DocumentMatch = z.object({
  /** Where the search terms were found. */
  fields: z.array(z.enum(['title', 'tags', 'category', 'notes', 'text'])),
  /** Short excerpt of the document text around the first match (only for text matches). */
  snippet: z.string().nullable(),
});
export type DocumentMatch = z.infer<typeof DocumentMatch>;

export const DocumentSummary = z.object({
  id: Id,
  title: z.string(),
  category: DocumentCategory,
  tags: z.array(z.string()),
  mimeType: z.string(),
  fileKind: z.enum(DOCUMENT_FILE_KINDS).nullable(),
  sizeBytes: z.number().int(),
  documentDate: IsoDate.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  uploadedBy: z.object({ id: Id, name: z.string() }).nullable(),
  ocrStatus: OcrStatus,
  hasThumbnail: z.boolean(),
  linkCount: z.number().int(),
  specialCategory: z.boolean(),
  deletedAt: IsoDateTime.nullable(),
  match: DocumentMatch.nullable().optional(),
});
export type DocumentSummary = z.infer<typeof DocumentSummary>;

export const DocumentLinkDto = z.object({
  id: Id,
  documentId: Id,
  entityType: EntityType,
  entityId: Id,
  /** Plain label without amounts or health details, e.g. "Gift · 12 Oct 2026". */
  label: z.string(),
  /** The record's own date when known. */
  date: IsoDate.nullable(),
  createdAt: IsoDateTime,
});
export type DocumentLinkDto = z.infer<typeof DocumentLinkDto>;

export const DocumentDetail = DocumentSummary.extend({
  notes: z.string().nullable(),
  originalFilename: z.string().nullable(),
  hasPreview: z.boolean(),
  hasText: z.boolean(),
  ocrError: z.string().nullable(),
  ocrCompletedAt: IsoDateTime.nullable(),
  hash: z.string().nullable(),
  links: z.array(DocumentLinkDto),
  /** Links to records the viewer cannot see (count only, never ids). */
  hiddenLinkCount: z.number().int(),
});
export type DocumentDetail = z.infer<typeof DocumentDetail>;

export const DOCUMENT_SORTS = ['newest', 'oldest', 'date_desc', 'date_asc', 'title', 'relevance'] as const;
export const DocumentSort = z.enum(DOCUMENT_SORTS);
export type DocumentSort = z.infer<typeof DocumentSort>;
export const DOCUMENT_SORT_LABELS: Record<DocumentSort, string> = {
  newest: 'Recently added',
  oldest: 'Oldest added',
  date_desc: 'Document date (newest first)',
  date_asc: 'Document date (oldest first)',
  title: 'Title (A to Z)',
  relevance: 'Best match',
};

/** Accept `?category=a&category=b`, `?category=a,b` or a single value. */
const csvArray = <T extends z.ZodType>(item: T) =>
  z.preprocess((v) => {
    if (v === undefined || v === null || v === '') return undefined;
    const arr = Array.isArray(v) ? v : [v];
    return arr.flatMap((x) => String(x).split(',')).map((s) => s.trim()).filter(Boolean);
  }, z.array(item).max(50).optional());

const boolish = z.preprocess(
  (v) => (v === 'true' || v === '1' || v === true ? true : v === 'false' || v === '0' || v === false ? false : v),
  z.boolean().optional(),
);

export const DocumentListQuery = z.object({
  q: z.string().trim().max(200).optional(),
  category: csvArray(DocumentCategory),
  tag: csvArray(z.string().trim().min(1).max(40)),
  ocrStatus: OcrStatus.optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  sort: DocumentSort.optional(),
  /** Owner/attorney only: list soft-deleted documents instead. */
  deleted: boolish,
  limit: z.coerce.number().int().min(1).max(100).default(24),
  offset: z.coerce.number().int().min(0).default(0),
});
export type DocumentListQuery = z.input<typeof DocumentListQuery>;

export const DocumentList = z.object({
  items: z.array(DocumentSummary),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type DocumentList = z.infer<typeof DocumentList>;

/** Multipart text fields sent with an upload (all strings on the wire). */
export const UploadDocumentFields = z.object({
  title: z.string().trim().max(200, 'Keep the title to 200 characters or fewer').optional(),
  category: DocumentCategory.default('other'),
  tags: z.preprocess((v) => parseTagsField(v), DocumentTags).default([]),
  documentDate: z.preprocess((v) => (v === '' ? undefined : v), IsoDate.optional()),
  notes: z.preprocess((v) => (v === '' ? undefined : v), DocumentNotes.optional()),
  /** Client-generated id for offline/idempotent uploads (ADR 0004). */
  clientId: z.preprocess((v) => (v === '' ? undefined : v), Id.optional()),
  /** Upload-and-link: attach the new document to this record straight away. */
  linkEntityType: z.preprocess((v) => (v === '' ? undefined : v), EntityType.optional()),
  linkEntityId: z.preprocess((v) => (v === '' ? undefined : v), Id.optional()),
});
export type UploadDocumentFields = z.input<typeof UploadDocumentFields>;

export const DuplicateDocument = z.object({
  id: Id,
  title: z.string(),
  category: DocumentCategory,
  createdAt: IsoDateTime,
});
export type DuplicateDocument = z.infer<typeof DuplicateDocument>;

export const UploadDocumentResponse = z.object({
  document: DocumentDetail,
  /** Other documents with exactly the same file contents (SHA-256), so the person can remove the copy. */
  duplicates: z.array(DuplicateDocument),
  /** false when an earlier upload with the same clientId was returned (idempotent retry). */
  created: z.boolean(),
});
export type UploadDocumentResponse = z.infer<typeof UploadDocumentResponse>;

export const DocumentDuplicatesQuery = z.object({ hash: z.string().regex(/^[a-f0-9]{64}$/, 'Expected a SHA-256 hash') });
export const DocumentDuplicatesResponse = z.object({ duplicates: z.array(DuplicateDocument) });
export type DocumentDuplicatesResponse = z.infer<typeof DocumentDuplicatesResponse>;

export const UpdateDocumentRequest = z
  .object({
    title: DocumentTitle.optional(),
    category: DocumentCategory.optional(),
    tags: DocumentTags.optional(),
    documentDate: IsoDate.nullable().optional(),
    notes: DocumentNotes.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateDocumentRequest = z.input<typeof UpdateDocumentRequest>;

export const DocumentDownloadQuery = z.object({ disposition: z.enum(['inline', 'attachment']).default('attachment') });
export const DocumentDownloadResponse = z.object({
  url: z.string(),
  expiresAt: IsoDateTime,
  disposition: z.enum(['inline', 'attachment']),
  mimeType: z.string(),
  filename: z.string(),
});
export type DocumentDownloadResponse = z.infer<typeof DocumentDownloadResponse>;

export const DocumentTextResponse = z.object({ status: OcrStatus, text: z.string().nullable(), error: z.string().nullable() });
export type DocumentTextResponse = z.infer<typeof DocumentTextResponse>;

export const DOCUMENT_RENDITION_KINDS = ['thumb', 'preview'] as const;
export const DocumentRenditionKind = z.enum(DOCUMENT_RENDITION_KINDS);
export type DocumentRenditionKind = z.infer<typeof DocumentRenditionKind>;

// ---------------------------------------------------------------- links (FR-2.3)

export const DocumentLinksQuery = z.object({ entityType: EntityType, entityId: Id });
export type DocumentLinksQuery = z.infer<typeof DocumentLinksQuery>;

export const CreateDocumentLinksRequest = z.object({
  entityType: EntityType,
  entityId: Id,
  documentIds: z.array(Id).min(1, 'Choose at least one document').max(50),
});
export type CreateDocumentLinksRequest = z.infer<typeof CreateDocumentLinksRequest>;

export const DeleteDocumentLinkQuery = DocumentLinksQuery.extend({ documentId: Id });
export type DeleteDocumentLinkQuery = z.infer<typeof DeleteDocumentLinkQuery>;

export const LinkedDocumentsResponse = z.object({ items: z.array(DocumentSummary) });
export type LinkedDocumentsResponse = z.infer<typeof LinkedDocumentsResponse>;

export const CreateDocumentLinksResponse = z.object({ linked: z.number().int(), alreadyLinked: z.number().int() });
export type CreateDocumentLinksResponse = z.infer<typeof CreateDocumentLinksResponse>;

export const LinkableRecordsQuery = z.object({ entityType: EntityType });
export const LinkableRecord = z.object({
  entityType: EntityType,
  entityId: Id,
  label: z.string(),
  date: IsoDate.nullable(),
});
export type LinkableRecord = z.infer<typeof LinkableRecord>;
export const LinkableRecordsResponse = z.object({ items: z.array(LinkableRecord) });
export type LinkableRecordsResponse = z.infer<typeof LinkableRecordsResponse>;

/** Record types that can be linked from the document side (the record must exist in the workspace). */
export const LINKABLE_ENTITY_TYPES = [
  'gift',
  'transaction',
  'account',
  'care_invoice',
  'care_event',
  'care_contract',
  'health_entry',
  'chc_process',
  'benefit_claim',
  'decision',
  'attorney_expense',
  'legal_instrument',
  'consent_record',
  'tax_year_summary',
] as const satisfies readonly EntityType[];

// ---------------------------------------------------------------- key documents checklist (FR-2.4)

export const KEY_DOCUMENT_STATUSES = ['present', 'missing', 'restricted'] as const;
export const KeyDocumentStatus = z.enum(KEY_DOCUMENT_STATUSES);
export type KeyDocumentStatus = z.infer<typeof KeyDocumentStatus>;

export const KeyDocumentItem = z.object({
  category: DocumentCategory,
  label: z.string(),
  /** `restricted` = the viewer can't see this category (it belongs to a hidden module). */
  status: KeyDocumentStatus,
  count: z.number().int(),
  latestDocumentId: Id.nullable(),
  latestDocumentTitle: z.string().nullable(),
  summary: z.string(),
});
export type KeyDocumentItem = z.infer<typeof KeyDocumentItem>;

export const KeyDocumentChecklist = z.object({
  stage: Stage,
  items: z.array(KeyDocumentItem),
  presentCount: z.number().int(),
  missingCount: z.number().int(),
  restrictedCount: z.number().int(),
});
export type KeyDocumentChecklist = z.infer<typeof KeyDocumentChecklist>;

/** Home summary (ARCHITECTURE §7 pattern). `available=false` when the member can't see documents. */
export const DocumentsSummary = z.object({
  available: z.boolean(),
  total: z.number().int().optional(),
  missingKeyDocuments: z.number().int().optional(),
  missingKeyCategories: z.array(DocumentCategory).optional(),
  textPending: z.number().int().optional(),
  recent: z.array(z.object({ id: Id, title: z.string(), category: DocumentCategory, createdAt: IsoDateTime })).optional(),
});
export type DocumentsSummary = z.infer<typeof DocumentsSummary>;

export const DocumentTagsResponse = z.object({ tags: z.array(z.object({ tag: z.string(), count: z.number().int() })) });
export type DocumentTagsResponse = z.infer<typeof DocumentTagsResponse>;
